import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    TableBody, TableCell, TableHead, TableRow,
    Typography, Box, Checkbox, FormControlLabel,
    Stack, Button, CircularProgress, Fade, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { getAdminLogs, rollbackVendor, getVendorPaymentMethods } from "../../services/vendorServices";
import { mergedTableHeadSx, mergedTableRowSx } from "../../utils/adminTableScrollSx";
import { usePermissions } from "../Context/permissionsProvider";
import AdminMergedTable from "../Shared/AdminMergedTable";
import AdminTablePagination from "../Shared/AdminTablePagination";
import ConfirmationModal from "../Shared/confirmationModal";
import { useUI } from "../Context/UIContext";

// Mappatura per tradurre le proprietà tecniche del database in italiano leggibile
const keyLabels = {
    businessName: "Nome Commerciale",
    tradeName: "Ragione Sociale",
    taxCode: "Codice Fiscale",
    vatNumber: "Partita IVA",
    phoneNumber: "Telefono principale",
    email: "Email principale",
    sftpServerUrl: "URL Server SFTP",
    pec: "PEC",
    sdiCode: "Codice SDI",
    stato: "Stato Account",
    username: "Username SFTP",
    password: "Password SFTP",
    referringName: "Nome Referente",
    referringSurname: "Cognome Referente",
    websiteUrl: "Sito Web",
    pubEmail: "Email Pubblica",
    refEmail: "Email Referente",
    refPhoneNumber: "Telefono Referente",
    indirizzo: "Indirizzo completo",
    via: "Via",
    civico: "Civico",
    cap: "CAP",
    comune: "Comune",
    provincia: "Provincia",
    regione: "Regione",
    nazione: "Nazione",
    paymentMethodIds: "Metodi di Pagamento"
};

// Campi sempre ignorati nel diff
const IGNORED_DIFF_KEYS = new Set(["idVendor", "count", "logId", "vendorStatus"]);

// Campi mostrati nel diff ma non ripristinabili tramite rollback
const NON_RESTORABLE_KEYS = new Set(["paymentMethodIds"]);

// Formatta il valore di un campo per la visualizzazione (i metodi di pagamento come nomi)
const formatDiffValue = (key, value, paymentMethodsMap) => {
    if (key === "paymentMethodIds") {
        if (!Array.isArray(value) || value.length === 0) return "Nessuno";
        return value.map((id) => paymentMethodsMap[id] || `#${id}`).join(", ");
    }
    return value === null || value === undefined ? "" : String(value);
};

// Verifica se un campo è cambiato (i metodi di pagamento sono array → confronto per contenuto)
const isKeyChanged = (key, oldVal, newVal) => {
    if (key === "paymentMethodIds") return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    return oldVal !== newVal;
};

// Elenco dei campi modificati e ripristinabili (esclude i campi non ripristinabili)
const getRestorableChangedKeys = (oldValues, newValues) => {
    if (!oldValues || !newValues) return [];
    return Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))
        .filter((key) => !IGNORED_DIFF_KEYS.has(key) && !NON_RESTORABLE_KEYS.has(key)
            && isKeyChanged(key, oldValues[key], newValues[key]) && (oldValues[key] || newValues[key]));
};

// Helper per calcolare le differenze e renderizzarle nello stile della piattaforma
const renderDiff = (oldValues, newValues, selectedFields = [], onToggleField = () => { }, paymentMethodsMap = {}) => {
    if (!oldValues || !newValues) return null;
    const diffs = [];

    const allKeys = Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]));

    allKeys.forEach(key => {
        if (IGNORED_DIFF_KEYS.has(key)) return;

        const oldVal = oldValues[key];
        const newVal = newValues[key];

        if (isKeyChanged(key, oldVal, newVal) && (oldVal || newVal)) {
            diffs.push({
                key,
                label: keyLabels[key] || key,
                oldVal: formatDiffValue(key, oldVal, paymentMethodsMap),
                newVal: formatDiffValue(key, newVal, paymentMethodsMap),
                restorable: !NON_RESTORABLE_KEYS.has(key)
            });
        }
    });

    if (diffs.length === 0) {
        return (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b' }}>
                Nessuna variazione rilevante rilevata nei dati.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {diffs.map((diff) => {
                const isChecked = diff.restorable && selectedFields.includes(diff.key);
                return (
                    <Box key={diff.key} sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        p: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                        transition: 'all 0.2s',
                        cursor: diff.restorable ? 'pointer' : 'default',
                        '&:hover': {
                            borderColor: diff.restorable ? '#0b3d87' : '#e2e8f0',
                            boxShadow: diff.restorable ? '0 4px 6px -1px rgba(0,0,0,0.05)' : '0 1px 3px rgba(0,0,0,0.02)'
                        }
                    }} onClick={() => { if (diff.restorable) onToggleField(diff.key); }}>
                        {/* Checkbox per ripristino parziale (assente per i campi non ripristinabili) */}
                        {diff.restorable ? (
                            <Box sx={{ mr: 1, mt: -0.5 }} onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={isChecked}
                                    onChange={() => onToggleField(diff.key)}
                                    sx={{
                                        color: '#cbd5e1',
                                        '&.Mui-checked': {
                                            color: '#0b3d87',
                                        }
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ width: 42 }} />
                        )}

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0b3d87', mb: 1 }}>
                                {diff.label}
                            </Typography>
                            <Stack spacing={1}>
                                {diff.oldVal && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#fee2e2', color: '#991b1b', px: 1.5, py: 0.75, borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>Valore Precedente:</Typography>
                                        <Typography component="span">{diff.oldVal}</Typography>
                                    </Box>
                                )}
                                {diff.newVal && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#dcfce7', color: '#166534', px: 1.5, py: 0.75, borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>Nuovo Valore:</Typography>
                                        <Typography component="span">{diff.newVal}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

// Funzione helper per gestire valori vuoti
const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
};

// Funzione helper per formattare la data
const formatDate = (value) => {
    if (!value) return "-";
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch (e) {
        console.error("Error formatting date:", e);
        return value;
    }
};

const StoricoAzioni = () => {
    const { isActionVisible } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    const { vendorId, businessName } = location.state || {};

    const { showSuccess, showLoading, showError } = useUI();
    const [rollbackOpen, setRollbackOpen] = useState(false);
    const [selectedLogId, setSelectedLogId] = useState(null);
    const [selectedFields, setSelectedFields] = useState([]);

    // Stati per la gestione della modale di dettaglio
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [activeLog, setActiveLog] = useState(null);

    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [paymentMethodsMap, setPaymentMethodsMap] = useState({});

    // Carica la mappa id -> nome dei metodi di pagamento per mostrarli nei log
    useEffect(() => {
        if (!vendorId) return undefined;
        let active = true;
        getVendorPaymentMethods(vendorId)
            .then((list) => {
                if (!active) return;
                const map = {};
                (list || []).forEach((m) => { map[m.id] = m.descrizione; });
                setPaymentMethodsMap(map);
            })
            .catch(() => { /* mappa non disponibile: si mostrano gli id */ });
        return () => { active = false; };
    }, [vendorId]);

    const handleOpenDetails = (log) => {
        setActiveLog(log);
        setDetailsOpen(true);
        setSelectedFields(getRestorableChangedKeys(log.oldValues, log.newValues));
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setActiveLog(null);
        setSelectedFields([]);
    };

    const handleToggleField = (key) => {
        setSelectedFields(prev => {
            if (prev.includes(key)) {
                return prev.filter(k => k !== key);
            } else {
                return [...prev, key];
            }
        });
    };

    const handleToggleAllFields = (log) => {
        if (!log.oldValues || !log.newValues) return;
        const allKeys = getRestorableChangedKeys(log.oldValues, log.newValues);

        if (selectedFields.length === allKeys.length) {
            setSelectedFields([]);
        } else {
            setSelectedFields(allKeys);
        }
    };

    const handleOpenRollback = (logId) => {
        setSelectedLogId(logId);
        setRollbackOpen(true);
    };

    const handleConfirmRollback = async () => {
        setRollbackOpen(false);
        setDetailsOpen(false);
        try {
            showLoading("Ripristino in corso...");
            await rollbackVendor(selectedLogId, selectedFields);
            showSuccess("Ripristino completato con successo!");
            fetchData(page);
            setActiveLog(null);
            setSelectedFields([]);
        } catch (error) {
            if (showError) {
                showError(error.message || "Errore durante il ripristino.");
            } else {
                console.error(error);
            }
        }
    };

    const fetchData = useCallback(async (currentPage = 0) => {
        setLoading(true);
        setFetchError(false);
        try {
            const data = await getAdminLogs(vendorId, currentPage, rowsPerPage);
            if (data?.logs) {
                setLogs(data.logs);
                setTotalCount(data.totalCount || 0);
            }
        } catch (error) {
            setFetchError(true);
            setErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    }, [vendorId, rowsPerPage]);

    useEffect(() => {
        fetchData(page);
    }, [fetchData, page]);

    const totalPages = Math.ceil(totalCount / rowsPerPage);

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', boxSizing: 'border-box' }}>
            {/* Header con pulsante torna indietro */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#0b3d87', backgroundColor: '#eef2ff', '&:hover': { backgroundColor: '#e0e7ff' } }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: "#0b3d87" }}>
                    Storico Azioni {businessName ? `- ${businessName}` : ""}
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', width: '100%', minHeight: '400px' }}>
                    <Fade in={loading}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 100,
                            borderRadius: 3
                        }}>
                            <CircularProgress size={60} thickness={4} sx={{ color: '#0b3d87' }} />
                        </Box>
                    </Fade>

                    <AdminMergedTable
                        tableLoading={loading}
                        head={(
                            <TableHead sx={mergedTableHeadSx(0)}>
                                <TableRow>
                                    <TableCell>Admin</TableCell>
                                    <TableCell>Azione</TableCell>
                                    <TableCell>Motivazione</TableCell>
                                    <TableCell align="center">Data</TableCell>
                                    <TableCell align="center">Ripristino</TableCell>
                                </TableRow>
                            </TableHead>
                        )}
                        body={(
                            <TableBody>
                                {fetchError || (logs.length === 0 && !loading) ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Box sx={{
                                                py: 8,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 2,
                                                minHeight: '250px'
                                            }}>
                                                {fetchError ? (
                                                    <>
                                                        <Box sx={{ backgroundColor: '#fff5f5', p: 2, borderRadius: '50%', display: 'flex' }}>
                                                            <CloudOffIcon sx={{ fontSize: 60, color: '#f44336' }} />
                                                        </Box>
                                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2d3748' }}>
                                                            Errore di caricamento
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#718096', maxWidth: '400px', mb: 1 }}>
                                                            {errorMessage || "Non siamo riusciti a recuperare lo storico azioni."}
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<RefreshIcon />}
                                                            onClick={() => fetchData(page)}
                                                            sx={{ backgroundColor: '#0b3d87', borderRadius: 2 }}
                                                        >
                                                            Riprova
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Box sx={{ backgroundColor: '#f7fafc', p: 2, borderRadius: '50%', display: 'flex' }}>
                                                            <SearchOffIcon sx={{ fontSize: 60, color: '#a0aec0' }} />
                                                        </Box>
                                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2d3748' }}>
                                                            Nessun log trovato
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#718096' }}>
                                                            Non ci sono azioni registrate per questo e-commerce.
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log, index) => {
                                        const hasChanges = log.hasOldValues;
                                        return (
                                            <TableRow
                                                key={log.logId || log.idAdminLog || `${log.dateTime}-${log.adminUsername}-${index}`}
                                                onClick={() => hasChanges && handleOpenDetails(log)}
                                                sx={{ ...mergedTableRowSx(index), cursor: hasChanges ? "pointer" : "default" }}
                                            >
                                                <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{formatValue(log.adminUsername)}</TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>{formatValue(log.operation)}</TableCell>
                                                <TableCell sx={{
                                                    fontStyle: log.motivation ? 'normal' : 'italic',
                                                    color: log.motivation ? '#475569' : 'text.disabled',
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                    maxWidth: "300px"
                                                }}>
                                                    {formatValue(log.motivation)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: '#64748b' }}>{formatDate(log.dateTime)}</TableCell>
                                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                    {hasChanges && isActionVisible("storicoAzioni", "dettagli") ? (
                                                        <IconButton
                                                            color="warning"
                                                            onClick={() => handleOpenDetails(log)}
                                                            title="Visualizza variazioni e ripristina"
                                                            sx={{
                                                                backgroundColor: '#fffdf0',
                                                                '&:hover': { backgroundColor: '#ffeec2' },
                                                                border: '1px solid #ffe8a3',
                                                                borderRadius: 2
                                                            }}
                                                        >
                                                            <SettingsBackupRestoreIcon />
                                                        </IconButton>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        )}
                    />
            </Box>

            <Box sx={{ mt: 2 }}>
                <AdminTablePagination
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    page={page}
                    setPage={setPage}
                    totalPages={Math.max(1, totalPages)}
                    countLabel="Azioni"
                    countValue={totalCount}
                />
            </Box>

            {/* Modale Dettagli Variazioni */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            p: 1
                        }
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#0b3d87' }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon sx={{ color: '#0b3d87' }} /> Dettaglio Variazioni
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetails}
                        sx={{ color: '#94a3b8' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {activeLog && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Riepilogo Dati Operazione */}
                            <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Operatore</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>{formatValue(activeLog.adminUsername)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Data Operazione</Typography>
                                        <Typography variant="body1" sx={{ color: '#334155' }}>{formatDate(activeLog.dateTime)}</Typography>
                                    </Box>
                                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Azione</Typography>
                                        <Typography variant="body1" sx={{ color: '#334155' }}>{formatValue(activeLog.operation)}</Typography>
                                    </Box>
                                    {activeLog.motivation && (
                                        <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Motivazione</Typography>
                                            <Typography variant="body1" sx={{ color: '#334155', whiteSpace: 'pre-wrap' }}>{activeLog.motivation}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Differenze Campi */}
                            {activeLog.oldValues && activeLog.newValues && (
                                <Box>
                                    {(() => {
                                        const allChangedKeys = getRestorableChangedKeys(activeLog.oldValues, activeLog.newValues);

                                        const isAllSelected = selectedFields.length === allChangedKeys.length;
                                        const isSomeSelected = selectedFields.length > 0 && selectedFields.length < allChangedKeys.length;

                                        return (
                                            <>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1, borderBottom: '1px solid #e2e8f0' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isAllSelected}
                                                                indeterminate={isSomeSelected}
                                                                onChange={() => handleToggleAllFields(activeLog)}
                                                                sx={{
                                                                    color: '#cbd5e1',
                                                                    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                                                        color: '#0b3d87',
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                Seleziona tutti i campi ({allChangedKeys.length})
                                                            </Typography>
                                                        }
                                                    />
                                                </Box>
                                                {renderDiff(activeLog.oldValues, activeLog.newValues, selectedFields, handleToggleField, paymentMethodsMap)}
                                            </>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCloseDetails} sx={{ color: '#475569', fontWeight: 'bold' }}>
                        Chiudi
                    </Button>
                    {activeLog && isActionVisible("storicoAzioni", "rollback") && (
                        <Button
                            variant="contained"
                            disabled={selectedFields.length === 0}
                            onClick={() => handleOpenRollback(activeLog.logId)}
                            startIcon={<SettingsBackupRestoreIcon />}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 'bold',
                                textTransform: 'none',
                                backgroundColor: '#0b3d87',
                                '&:hover': {
                                    backgroundColor: '#0b3d87'
                                },
                                boxShadow: '0 4px 12px -2px rgba(35, 87, 244, 0.3)'
                            }}
                        >
                            {(() => {
                                const allChangedKeys = getRestorableChangedKeys(activeLog.oldValues, activeLog.newValues);
                                return selectedFields.length === allChangedKeys.length
                                    ? "Ripristina tutti i campi"
                                    : `Ripristina campi selezionati (${selectedFields.length})`;
                            })()}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmationModal
                open={rollbackOpen}
                onClose={() => setRollbackOpen(false)}
                onConfirm={handleConfirmRollback}
                title="Conferma Ripristino"
                subtitle="Vuoi procedere al rollback?"
                message={selectedFields.length === 0 ? "" : `Ripristinando, i dati dell'e-commerce verranno riportati allo stato precedente per i seguenti campi selezionati (${selectedFields.length}).`}
                confirmText="RIPRISTINA"
                cancelText="ANNULLA"
                confirmColor="#0b3d87"
                icon={<SettingsBackupRestoreIcon sx={{ color: "white", fontSize: 50 }} />}
            />
        </Box>
    );
};

export default StoricoAzioni;
 